import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2, X, Search, Camera, Wand2, Package, RefreshCw, Download } from "lucide-react";
import Layout from "../components/Layout";
import EraseAllButton from "../components/EraseAllButton";
import BarcodeScanner from "../components/BarcodeScanner";
import { api } from "../lib/api";

const newItem = () => ({
  title: "",
  console: "",
  components: "Game in Box with Manual",
  condition: "Excellent",
  barcode: "",
});

const newNonGame = () => ({ title: "", sale_price: "", quantity: 1, sku: "" });

// Pop-K tier ladder (mirrors pricing.INVENTORY_POP_TIER_LADDER in the backend).
// Operator-facing: every K decimal has a whole-number tier 0..5.
// Tier 0 (K=0.01) is admin-set only — never produced automatically.
const POP_TIER_TO_K = { 0: 0.01, 1: 0.30, 2: 0.50, 3: 0.70, 4: 0.85, 5: 0.95 };
const MIN_POP_TIER = 0;
const MAX_POP_TIER = 5;
const kToPopTier = (k) => {
  if (k == null) return 2;
  const num = Number(k);
  let bestTier = 2;
  let bestDiff = Infinity;
  for (const [t, kk] of Object.entries(POP_TIER_TO_K)) {
    const d = Math.abs(kk - num);
    if (d < bestDiff) { bestDiff = d; bestTier = Number(t); }
  }
  return bestTier;
};
const popTierToK = (t) => {
  const clamped = Math.max(MIN_POP_TIER, Math.min(MAX_POP_TIER, Number(t)));
  return POP_TIER_TO_K[clamped];
};

export default function InventoryAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_value: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addRows, setAddRows] = useState([newItem()]);
  const [adding, setAdding] = useState(false);
  const [consoles, setConsoles] = useState([]);
  const [opts, setOpts] = useState({ conditions: [], components: [] });
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState({});

  // barcode scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerRowIdx, setScannerRowIdx] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(null); // idx of the row currently being looked up

  // non-game item modal state
  const [showAddNonGame, setShowAddNonGame] = useState(false);
  const [nonGameRows, setNonGameRows] = useState([newNonGame()]);
  const [addingNonGame, setAddingNonGame] = useState(false);
  const [nonGameErr, setNonGameErr] = useState("");
  const [qtyEditId, setQtyEditId] = useState(null);
  const [qtyEditVal, setQtyEditVal] = useState("");

  // PC URL inline override
  const [pcUrlOpen, setPcUrlOpen] = useState(null);   // inventory row id
  const [pcUrlInput, setPcUrlInput] = useState("");
  const [pcUrlBusy, setPcUrlBusy] = useState(null);
  const [pcUrlErr, setPcUrlErr] = useState("");

  const openPcUrlEditor = (it) => {
    setPcUrlOpen(it.id);
    setPcUrlInput(it.pc_url || "");
    setPcUrlErr("");
  };
  const closePcUrlEditor = () => {
    setPcUrlOpen(null);
    setPcUrlInput("");
    setPcUrlErr("");
  };
  const applyPcUrlOverride = async (id) => {
    const url = (pcUrlInput || "").trim();
    if (!url) return;
    setPcUrlBusy(id);
    setPcUrlErr("");
    try {
      await api.post(`/inventory/${id}/pc-url`, { pc_url: url });
      closePcUrlEditor();
      load();
    } catch (e) {
      setPcUrlErr(e.response?.data?.detail || "Failed to apply URL");
    } finally {
      setPcUrlBusy(null);
    }
  };

  // ── Bulk selection + bulk Override-Tier apply ──────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTier, setBulkTier] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const toggleSelected = (id) => {
    setSelectedIds((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectableItems = items.filter((it) => !it.is_non_game);
  const allSelected = selectableItems.length > 0 && selectableItems.every((it) => selectedIds.has(it.id));
  const toggleSelectAll = () => {
    setSelectedIds((p) => {
      if (allSelected) return new Set();
      const next = new Set(p);
      selectableItems.forEach((it) => next.add(it.id));
      return next;
    });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setBulkMsg(""); };

  const applyBulkTier = async (clearInstead = false) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const payload = { ids: Array.from(selectedIds) };
      if (clearInstead) {
        payload.pop_k_override = null;
      } else {
        const tier = Number(bulkTier);
        if (!Number.isInteger(tier) || tier < MIN_POP_TIER || tier > MAX_POP_TIER) {
          setBulkMsg(`Tier must be a whole number ${MIN_POP_TIER}–${MAX_POP_TIER}.`);
          setBulkBusy(false);
          return;
        }
        payload.pop_k_override = popTierToK(tier);
      }
      const r = await api.patch("/inventory/bulk/pop-override", payload);
      const parts = [];
      if (r.data?.updated) parts.push(`Updated ${r.data.updated}`);
      if (r.data?.skipped) parts.push(`${r.data.skipped} skipped (non-game)`);
      setBulkMsg(parts.join(" · ") || "Done");
      setSelectedIds(new Set());
      setBulkTier("");
      load();
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const applyBulkResync = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    setBulkMsg("Resyncing — this may take a minute for large batches…");
    try {
      const r = await api.patch("/inventory/bulk/resync-pc", { ids: Array.from(selectedIds) });
      const parts = [];
      if (r.data?.updated) parts.push(`Resynced ${r.data.updated}`);
      if (r.data?.failed) parts.push(`${r.data.failed} couldn't be resolved`);
      if (r.data?.skipped) parts.push(`${r.data.skipped} skipped (non-game)`);
      setBulkMsg(parts.join(" · ") || "Done");
      setSelectedIds(new Set());
      load();
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || "Resync failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const updateAddRow = (idx, field, val) => {
    setAddRows((p) => p.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const lookupBarcode = async (idx) => {
    const row = addRows[idx];
    if (!row?.barcode?.trim()) return;
    setLookupBusy(idx);
    try {
      const r = await api.get(`/admin/lookup-barcode`, { params: { barcode: row.barcode.trim() } });
      if (r.data.found) {
        setAddRows((p) => {
          // 1. Fill the current row's resolved title + console.
          const updated = p.map((rr, i) => i === idx ? {
            ...rr,
            title: r.data.title || rr.title,
            console: r.data.console || rr.console,
          } : rr);
          // 2. Auto-append a fresh empty row IF this is the last row, so the
          //    operator can immediately scan the next barcode without pressing
          //    "Add Another".
          if (idx === updated.length - 1) {
            updated.push(newItem());
          }
          return updated;
        });
        // Focus the next row's barcode field (after React has rendered the new row)
        focusBarcode(idx + 1);
      } else {
        alert(`No game found for barcode "${row.barcode}". You can fill in title + console manually.`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLookupBusy(null);
    }
  };

  const onScanned = async (code) => {
    setScannerOpen(false);
    if (scannerRowIdx == null) return;
    updateAddRow(scannerRowIdx, "barcode", code);
    // Auto-run lookup once we've populated the barcode
    setTimeout(() => lookupBarcode(scannerRowIdx), 50);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory", { params: search ? { q: search } : {} });
      setItems(r.data);
      // Summary is best-effort — if the endpoint is missing (older backend) we
      // still want the listing to render, so don't let it throw.
      try {
        const s = await api.get("/inventory/summary");
        setSummary(s.data);
      } catch {
        // Fall back to summing the currently-visible page
        const count = r.data.length;
        const total = r.data.reduce((sum, it) => sum + Number(it.sale_price || 0), 0);
        setSummary({ count, total_value: total });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/consoles").then((r) => setConsoles(r.data));
    api.get("/options").then((r) => setOpts(r.data));
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (showAdd) focusBarcode(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdd]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/inventory/${id}`);
    setItems((p) => p.filter((it) => it.id !== id));
  };

  const saveEdit = async (id) => {
    const body = {
      pc_price: editVal.pc_price != null && editVal.pc_price !== "" ? Number(editVal.pc_price) : undefined,
      sale_price: editVal.sale_price != null && editVal.sale_price !== "" ? Number(editVal.sale_price) : undefined,
      barcode: editVal.barcode || undefined,
      condition: editVal.condition || undefined,
      components: editVal.components || undefined,
    };
    // Override semantics: empty string ⇒ clear, otherwise convert the
    // operator-friendly tier (0..5) back to the decimal K the backend stores.
    if (Object.prototype.hasOwnProperty.call(editVal, "pop_tier_override")) {
      const raw = editVal.pop_tier_override;
      if (raw === "" || raw == null) {
        body.clear_pop_k_override = true;
      } else {
        body.pop_k_override = popTierToK(raw);
      }
    }
    await api.patch(`/inventory/${id}`, body);
    setEditId(null);
    setEditVal({});
    load();
  };

  // Add inventory form
  const barcodeRefs = useRef({});
  const focusBarcode = (idx) => {
    setTimeout(() => barcodeRefs.current[idx]?.focus(), 30);
  };
  const addItem = () => {
    const nextIdx = addRows.length;
    setAddRows((p) => [...p, newItem()]);
    focusBarcode(nextIdx);
  };
  const removeAddRow = (i) => setAddRows((p) => p.filter((_, idx) => idx !== i));

  const confirmAdd = async () => {
    // Drop any rows the operator left empty (e.g. the trailing scaffold row
    // that auto-appended after the last scan).
    const filled = addRows.filter((r) => (r.barcode || "").trim() || (r.title || "").trim());
    if (filled.length === 0) {
      setShowAdd(false);
      setAddRows([newItem()]);
      return;
    }
    setAdding(true);
    try {
      await api.post("/inventory", filled.map((r) => ({ ...r, barcode: r.barcode || null })));
      setShowAdd(false);
      setAddRows([newItem()]);
      load();
    } finally {
      setAdding(false);
    }
  };

  // Non-game item add flow
  const updateNonGameRow = (idx, field, val) => {
    setNonGameRows((p) => p.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };
  const addNonGameRow = () => setNonGameRows((p) => [...p, newNonGame()]);
  const removeNonGameRow = (i) => setNonGameRows((p) => p.filter((_, idx) => idx !== i));
  const confirmAddNonGame = async () => {
    setNonGameErr("");
    const cleaned = nonGameRows.map((r) => ({
      title: (r.title || "").trim(),
      sale_price: Number(r.sale_price),
      quantity: Number(r.quantity),
      sku: (r.sku || "").trim(),
    }));
    for (const r of cleaned) {
      if (!r.title) return setNonGameErr("Every row needs a name.");
      if (!r.sku) return setNonGameErr("Every row needs an SKU.");
      if (!(r.sale_price > 0)) return setNonGameErr("Sale price must be greater than 0.");
      if (!Number.isInteger(r.quantity) || r.quantity < 1)
        return setNonGameErr("Quantity must be a whole number ≥ 1.");
    }
    setAddingNonGame(true);
    try {
      await api.post("/inventory/non-game", cleaned);
      setShowAddNonGame(false);
      setNonGameRows([newNonGame()]);
      load();
    } catch (e) {
      setNonGameErr(e.response?.data?.detail || "Failed to add non-game items.");
    } finally {
      setAddingNonGame(false);
    }
  };

  const saveQty = async (id) => {
    const qty = Math.max(0, Math.floor(Number(qtyEditVal)));
    if (!Number.isFinite(qty)) return;
    await api.patch(`/inventory/${id}/quantity`, { quantity: qty });
    setQtyEditId(null);
    setQtyEditVal("");
    load();
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/pos/dashboard")} className="label-eyebrow mb-6 inline-block" data-testid="inv-back">← Back</button>
        <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
          <h1 className="font-display text-4xl font-black">Inventory</h1>
          <div className="flex gap-2">
            <button className="btn-primary inline-flex items-center gap-2" onClick={() => setShowAdd(true)} data-testid="inv-add-btn"><Plus size={14} /> Add Inventory</button>
            <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setShowAddNonGame(true)} data-testid="inv-add-nongame-btn"><Package size={14} /> Add Non-Game Item</button>
            <EraseAllButton scope="inventory" label="Erase All Inventory" confirmText="every inventory item" onDone={load} testid="inv-erase-all" />
          </div>
        </div>

        {/* Stat strip — total inventory value */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <div className="surface p-5">
            <div className="label-eyebrow mb-2">Items in Stock</div>
            <div className="font-mono font-black text-3xl" data-testid="inv-stat-count">
              {summary.count.toLocaleString()}
            </div>
          </div>
          <div className="surface p-5">
            <div className="label-eyebrow mb-2">Total Inventory Value (Sale $)</div>
            <div className="font-mono font-black text-3xl" style={{ color: "var(--ab-primary)" }} data-testid="inv-stat-value">
              ${summary.total_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        <form onSubmit={onSearch} className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="surface flex items-stretch flex-1 max-w-md">
            <input className="flex-1 bg-transparent border-0 px-4 py-3 outline-none font-mono" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title / console / barcode…" data-testid="inv-search-input" />
            <button type="submit" className="px-5" style={{ background: "var(--ab-surface-2)" }} data-testid="inv-search-btn"><Search size={16} /></button>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await api.get("/inventory/export.xlsx", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `ab_microplay_inventory_${new Date().toISOString().slice(0,10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                alert(e.response?.data?.detail || "Export failed");
              }
            }}
            className="btn-secondary inline-flex items-center gap-2 text-xs"
            data-testid="inv-export-xlsx"
            title="Download every item as an Excel spreadsheet"
          >
            <Download size={14} /> Export Excel
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await api.get("/inventory/export-purchase.xlsx", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `ab_microplay_for_purchase_${new Date().toISOString().slice(0,10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                alert(e.response?.data?.detail || "Export failed");
              }
            }}
            className="btn-secondary inline-flex items-center gap-2 text-xs"
            data-testid="inv-export-purchase"
            title="Export every game (Item Name, Console, Condition, Components, PC Price) for purchase planning"
          >
            <Download size={14} /> Export for Purchase
          </button>
        </form>

        {loading ? (
          <Loader2 className="animate-spin" />
        ) : (
          <>
          {selectedIds.size > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-3 px-4 py-3" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid var(--ab-primary)" }} data-testid="inv-bulk-bar">
              <span className="label-eyebrow" style={{ color: "var(--ab-primary)" }} data-testid="inv-bulk-count">
                {selectedIds.size} selected
              </span>
              <span className="text-xs" style={{ color: "var(--ab-text-muted)" }}>Set Override Tier to:</span>
              <input
                type="number"
                min={MIN_POP_TIER}
                max={MAX_POP_TIER}
                step="1"
                value={bulkTier}
                onChange={(e) => setBulkTier(e.target.value)}
                className="input-terminal"
                style={{ padding: "4px 8px", fontSize: 12, width: 60 }}
                placeholder="0–5"
                data-testid="inv-bulk-tier-input"
              />
              <button
                onClick={() => applyBulkTier(false)}
                disabled={bulkBusy || bulkTier === ""}
                className="btn-primary"
                style={{ padding: "4px 12px", fontSize: 11 }}
                data-testid="inv-bulk-apply"
              >
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : "Apply to selected"}
              </button>
              <button
                onClick={() => applyBulkTier(true)}
                disabled={bulkBusy}
                className="btn-secondary"
                style={{ padding: "4px 12px", fontSize: 11 }}
                data-testid="inv-bulk-clear-override"
                title="Remove the override tier from the selected rows (revert to auto)"
              >
                Clear override
              </button>
              <button
                onClick={applyBulkResync}
                disabled={bulkBusy}
                className="btn-secondary inline-flex items-center gap-1"
                style={{ padding: "4px 12px", fontSize: 11, borderColor: "var(--ab-primary)", color: "var(--ab-primary)" }}
                data-testid="inv-bulk-resync"
                title="Re-pull pricing data from PriceCharting for the selected rows"
              >
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Resync from PC
              </button>
              <button
                onClick={async () => {
                  try {
                    const r = await api.post(
                      "/inventory/export-selected.xlsx",
                      { ids: Array.from(selectedIds) },
                      { responseType: "blob" }
                    );
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `ab_microplay_inventory_selected_${new Date().toISOString().slice(0,10)}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    alert(e.response?.data?.detail || "Export failed");
                  }
                }}
                disabled={bulkBusy}
                className="btn-secondary inline-flex items-center gap-1"
                style={{ padding: "4px 12px", fontSize: 11 }}
                data-testid="inv-bulk-export-selected"
                title="Download an .xlsx of the selected rows (full inventory format)"
              >
                <Download size={12} /> Export Selected
              </button>
              <button
                onClick={async () => {
                  try {
                    const r = await api.post(
                      "/inventory/export-selected-purchase.xlsx",
                      { ids: Array.from(selectedIds) },
                      { responseType: "blob" }
                    );
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `ab_microplay_for_purchase_selected_${new Date().toISOString().slice(0,10)}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    alert(e.response?.data?.detail || "Export failed");
                  }
                }}
                disabled={bulkBusy}
                className="btn-secondary inline-flex items-center gap-1"
                style={{ padding: "4px 12px", fontSize: 11 }}
                data-testid="inv-bulk-export-selected-purchase"
                title="Download an .xlsx of the selected rows (purchase-planning format)"
              >
                <Download size={12} /> Export Selected (Purchase)
              </button>
              <button
                onClick={clearSelection}
                className="label-eyebrow"
                style={{ marginLeft: "auto" }}
                data-testid="inv-bulk-cancel"
              >
                Cancel
              </button>
              {bulkMsg && (
                <div className="w-full text-xs" style={{ color: "var(--ab-text-muted)" }} data-testid="inv-bulk-msg">{bulkMsg}</div>
              )}
            </div>
          )}

          <div className="surface overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="label-eyebrow" style={{ background: "var(--ab-surface-2)" }}>
                  <th className="px-3 py-3" style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      data-testid="inv-select-all"
                      title="Select all game rows on this page"
                    />
                  </th>
                  <th className="text-left px-4 py-3">Item</th>
                  <th className="text-left px-4 py-3">Console / Type</th>
                  <th className="text-left px-4 py-3">Cond.</th>
                  <th className="text-right px-4 py-3">PC Price</th>
                  <th className="text-right px-4 py-3">Pop. Tier</th>
                  <th className="text-right px-4 py-3">Override Tier</th>
                  <th className="text-right px-4 py-3">Sale $</th>
                  <th className="text-right px-4 py-3">Qty</th>
                  <th className="text-left px-4 py-3">Barcode / SKU</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-8 text-center" style={{ color: "var(--ab-text-dim)" }} data-testid="inv-empty">No inventory yet.</td></tr>
                )}
                {items.map((it) => {
                  const isEditing = editId === it.id;
                  const isQtyEditing = qtyEditId === it.id;
                  const hasOverride = it.pop_k_override != null;
                  const nonGame = !!it.is_non_game;
                  return (
                    <React.Fragment key={it.id}>
                    <tr className="border-t" style={{ borderColor: "var(--ab-border)" }} data-testid={`inv-row-${it.id}`}>
                      <td className="px-3 py-3">
                        {!nonGame && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(it.id)}
                            onChange={() => toggleSelected(it.id)}
                            data-testid={`inv-select-${it.id}`}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 font-bold">
                        {it.title}
                        {nonGame && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest font-mono" style={{ color: "var(--ab-primary)" }} data-testid={`inv-tag-nongame-${it.id}`}>
                            Non-Game
                          </span>
                        )}
                        {!nonGame && (
                          <div className="text-[10px] mt-0.5 flex gap-2 items-center" style={{ color: "var(--ab-text-dim)" }}>
                            {it.pc_url && <a href={it.pc_url} target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--ab-primary)" }} data-testid={`inv-pc-link-${it.id}`}>PC ↗</a>}
                            <button
                              type="button"
                              onClick={() => pcUrlOpen === it.id ? closePcUrlEditor() : openPcUrlEditor(it)}
                              className="underline"
                              data-testid={`inv-pc-url-toggle-${it.id}`}
                            >
                              {pcUrlOpen === it.id ? "cancel" : (it.pc_url ? "edit URL" : "set URL")}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: "var(--ab-text-muted)" }}>{it.console}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: "var(--ab-text-muted)" }}>
                        {nonGame ? "—" : (isEditing ? (
                          <div className="flex flex-col gap-1">
                            <select
                              className="input-terminal"
                              style={{ padding: "2px 4px", fontSize: 11 }}
                              value={editVal.condition ?? it.condition}
                              onChange={(e) => setEditVal({ ...editVal, condition: e.target.value })}
                              data-testid={`inv-edit-condition-${it.id}`}
                            >
                              {["New","Like New","Excellent","Acceptable","Poor","Sport","Not Working"].map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            <select
                              className="input-terminal"
                              style={{ padding: "2px 4px", fontSize: 11 }}
                              value={editVal.components ?? it.components}
                              onChange={(e) => setEditVal({ ...editVal, components: e.target.value })}
                              data-testid={`inv-edit-components-${it.id}`}
                            >
                              <option value="Game in Box with Manual">CIB</option>
                              <option value="Item & Box">Item & Box</option>
                              <option value="Game with missing Components">Loose</option>
                            </select>
                          </div>
                        ) : (<>{it.condition}<br />{it.components === "Game in Box with Manual" ? "CIB" : it.components === "Game with missing Components" ? "Loose" : (it.components || "—")}</>))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {nonGame ? "—" : (isEditing ? <input className="input-terminal text-right" style={{ padding: "4px 6px", fontSize: 12, width: 70 }} type="number" value={editVal.pc_price ?? it.pc_price ?? ""} onChange={(e) => setEditVal({ ...editVal, pc_price: e.target.value })} data-testid="inv-edit-pc" />
                        : it.pc_price != null ? `$${Number(it.pc_price).toFixed(2)}` : "—")}
                      </td>
                      <td className="px-4 py-3 text-right font-mono" style={{ color: "var(--ab-text-muted)" }}>{nonGame ? "—" : (it.popularity_constant != null ? kToPopTier(it.popularity_constant) : "—")}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        {nonGame ? "—" : (isEditing ? (
                          <input
                            className="input-terminal text-right"
                            style={{ padding: "4px 6px", fontSize: 12, width: 70 }}
                            type="number"
                            step="1"
                            min={MIN_POP_TIER}
                            max={MAX_POP_TIER}
                            placeholder={String(kToPopTier(it.popularity_constant))}
                            value={editVal.pop_tier_override ?? (it.pop_k_override != null ? kToPopTier(it.pop_k_override) : "")}
                            onChange={(e) => setEditVal({ ...editVal, pop_tier_override: e.target.value })}
                            data-testid="inv-edit-override"
                          />
                        ) : hasOverride ? (
                          <span style={{ color: "var(--ab-warning)", fontWeight: 700 }} title={`Manually overridden (K=${Number(it.pop_k_override).toFixed(2)})`}>{kToPopTier(it.pop_k_override)}</span>
                        ) : (
                          <span style={{ color: "var(--ab-text-dim)" }}>{it.popularity_constant != null ? kToPopTier(it.popularity_constant) : "—"}</span>
                        ))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold" style={{ color: "var(--ab-primary)" }}>
                        {isEditing && !nonGame ? <input className="input-terminal text-right" style={{ padding: "4px 6px", fontSize: 12, width: 70 }} type="number" value={editVal.sale_price ?? it.sale_price} onChange={(e) => setEditVal({ ...editVal, sale_price: e.target.value })} data-testid="inv-edit-sale" />
                        : `$${Number(it.sale_price).toFixed(0)}`}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {nonGame ? (
                          isQtyEditing ? (
                            <input
                              className="input-terminal text-right"
                              style={{ padding: "4px 6px", fontSize: 12, width: 60 }}
                              type="number"
                              min="0"
                              value={qtyEditVal}
                              onChange={(e) => setQtyEditVal(e.target.value)}
                              data-testid={`inv-qty-input-${it.id}`}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setQtyEditId(it.id); setQtyEditVal(String(it.quantity ?? 0)); }}
                              className="font-mono"
                              style={{ color: "var(--ab-text)", textDecoration: "underline dotted", cursor: "pointer" }}
                              data-testid={`inv-qty-${it.id}`}
                            >
                              {Number(it.quantity ?? 0)}
                            </button>
                          )
                        ) : (
                          <span style={{ color: "var(--ab-text-dim)" }}>1</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {isEditing ? <input className="input-terminal" style={{ padding: "4px 6px", fontSize: 12, width: 110 }} value={editVal.barcode ?? it.barcode ?? ""} onChange={(e) => setEditVal({ ...editVal, barcode: e.target.value })} data-testid="inv-edit-barcode" />
                        : it.barcode || "—"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isQtyEditing ? (
                          <>
                            <button className="btn-primary" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => saveQty(it.id)} data-testid={`inv-qty-save-${it.id}`}>Save Qty</button>
                            <button className="btn-secondary ml-1" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => { setQtyEditId(null); setQtyEditVal(""); }}>Cancel</button>
                          </>
                        ) : isEditing ? (
                          <>
                            <button className="btn-primary" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => saveEdit(it.id)} data-testid={`inv-save-${it.id}`}>Save</button>
                            <button className="btn-secondary ml-1" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => { setEditId(null); setEditVal({}); }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            {!nonGame && (
                              <button className="btn-secondary" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => { setEditId(it.id); setEditVal({}); }} data-testid={`inv-edit-${it.id}`}>Edit</button>
                            )}
                            <button className="ml-1 btn-danger" style={{ padding: "4px 10px", fontSize: 10 }} onClick={() => del(it.id)} data-testid={`inv-del-${it.id}`}><Trash2 size={12} /></button>
                          </>
                        )}
                      </td>
                    </tr>
                    {pcUrlOpen === it.id && (
                      <tr style={{ background: "rgba(37,99,235,0.06)" }} data-testid={`inv-pc-url-row-${it.id}`}>
                        <td colSpan={11} className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="label-eyebrow" style={{ color: "var(--ab-primary)" }}>Override PC URL</span>
                            <input
                              className="input-terminal flex-1 min-w-[280px]"
                              style={{ padding: "6px 10px", fontSize: 12 }}
                              placeholder="https://www.pricecharting.com/game/..."
                              value={pcUrlInput}
                              onChange={(e) => setPcUrlInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPcUrlOverride(it.id); } }}
                              data-testid={`inv-pc-url-input-${it.id}`}
                            />
                            <button
                              type="button"
                              onClick={() => applyPcUrlOverride(it.id)}
                              disabled={pcUrlBusy === it.id || !pcUrlInput.trim()}
                              className="btn-primary inline-flex items-center gap-2"
                              style={{ padding: "6px 12px", fontSize: 11 }}
                              data-testid={`inv-pc-url-apply-${it.id}`}
                            >
                              {pcUrlBusy === it.id ? <Loader2 size={12} className="animate-spin" /> : null} Apply
                            </button>
                            {pcUrlErr && pcUrlOpen === it.id && (
                              <span className="text-xs" style={{ color: "var(--ab-danger)" }} data-testid={`inv-pc-url-err-${it.id}`}>{pcUrlErr}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        {showAdd && (
          <div className="fixed inset-0 z-50 flex items-start justify-center px-6 py-10 overflow-auto" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => setShowAdd(false)}>
            <div className="surface p-6 max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-display text-2xl font-bold">Add Inventory</h3>
                <button onClick={() => setShowAdd(false)} data-testid="inv-add-close"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                {addRows.map((r, idx) => (
                  <div key={idx} className="border p-3 grid sm:grid-cols-12 gap-2 items-end" style={{ borderColor: "var(--ab-border)" }} data-testid={`inv-add-row-${idx}`}>
                    <div className="sm:col-span-4"><label className="label-eyebrow block mb-1">Title</label><input className="input-terminal" value={r.title} onChange={(e) => updateAddRow(idx, "title", e.target.value)} data-testid={`inv-add-title-${idx}`} /></div>
                    <div className="sm:col-span-3"><label className="label-eyebrow block mb-1">Console</label>
                      <select className="input-terminal" value={r.console} onChange={(e) => updateAddRow(idx, "console", e.target.value)} data-testid={`inv-add-console-${idx}`}>
                        <option value="">Select…</option>
                        {consoles.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-3"><label className="label-eyebrow block mb-1">Components</label>
                      <select className="input-terminal" value={r.components} onChange={(e) => updateAddRow(idx, "components", e.target.value)} data-testid={`inv-add-components-${idx}`}>
                        {opts.components.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Condition</label>
                      <select className="input-terminal" value={r.condition} onChange={(e) => updateAddRow(idx, "condition", e.target.value)} data-testid={`inv-add-condition-${idx}`}>
                        {opts.conditions.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-12">
                      <label className="label-eyebrow block mb-1">Barcode (scan or enter to auto-fill title + console)</label>
                      <div className="flex flex-wrap gap-2">
                        <input
                          ref={(el) => { if (el) barcodeRefs.current[idx] = el; }}
                          className="input-terminal flex-1 min-w-[180px]"
                          placeholder="e.g. 045496870010"
                          value={r.barcode}
                          onChange={(e) => updateAddRow(idx, "barcode", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupBarcode(idx); } }}
                          data-testid={`inv-add-barcode-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => lookupBarcode(idx)}
                          disabled={lookupBusy === idx || !r.barcode?.trim()}
                          className="btn-secondary inline-flex items-center gap-2"
                          data-testid={`inv-add-lookup-${idx}`}
                        >
                          {lookupBusy === idx ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Look up
                        </button>
                        <button
                          type="button"
                          onClick={() => { setScannerRowIdx(idx); setScannerOpen(true); }}
                          className="btn-secondary inline-flex items-center gap-2"
                          data-testid={`inv-add-scan-${idx}`}
                        >
                          <Camera size={14} /> Scan
                        </button>
                      </div>
                    </div>
                    <div className="sm:col-span-12 flex justify-end">
                      {addRows.length > 1 && <button onClick={() => removeAddRow(idx)} className="label-eyebrow hover:text-[var(--ab-danger)]" data-testid={`inv-add-remove-${idx}`}>Remove</button>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-3 justify-between">
                <button onClick={addItem} className="btn-secondary inline-flex items-center gap-2" data-testid="inv-add-row-btn"><Plus size={14} /> Add Another</button>
                <button onClick={confirmAdd} disabled={adding} className="btn-primary inline-flex items-center gap-2" data-testid="inv-add-confirm">{adding ? <Loader2 size={14} className="animate-spin" /> : "Confirm Inventory"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showAddNonGame && (
        <div className="fixed inset-0 z-50 flex items-start justify-center px-6 py-10 overflow-auto" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => setShowAddNonGame(false)} data-testid="inv-add-nongame-modal">
          <div className="surface p-6 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-display text-2xl font-bold inline-flex items-center gap-2"><Package size={20} style={{ color: "var(--ab-primary)" }} /> Add Non-Game Item</h3>
              <button onClick={() => setShowAddNonGame(false)} data-testid="inv-add-nongame-close"><X size={18} /></button>
            </div>
            <p className="text-xs mb-4" style={{ color: "var(--ab-text-muted)" }}>
              Use this for accessories, merch, or anything that isn't a tracked game. SKUs must be unique across <b>all</b> inventory (games + non-game) and act as the scannable barcode at the POS.
            </p>
            <div className="space-y-3">
              {nonGameRows.map((r, idx) => (
                <div key={idx} className="border p-3 grid sm:grid-cols-12 gap-2 items-end" style={{ borderColor: "var(--ab-border)" }} data-testid={`inv-nongame-row-${idx}`}>
                  <div className="sm:col-span-5">
                    <label className="label-eyebrow block mb-1">Name</label>
                    <input className="input-terminal" value={r.title} onChange={(e) => updateNonGameRow(idx, "title", e.target.value)} placeholder="e.g. SNES Controller" data-testid={`inv-nongame-title-${idx}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-eyebrow block mb-1">Sale Price ($)</label>
                    <input className="input-terminal" type="number" min="0" step="0.01" value={r.sale_price} onChange={(e) => updateNonGameRow(idx, "sale_price", e.target.value)} data-testid={`inv-nongame-price-${idx}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-eyebrow block mb-1">Quantity</label>
                    <input className="input-terminal" type="number" min="1" step="1" value={r.quantity} onChange={(e) => updateNonGameRow(idx, "quantity", e.target.value)} data-testid={`inv-nongame-qty-${idx}`} />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="label-eyebrow block mb-1">SKU (barcode)</label>
                    <input className="input-terminal" value={r.sku} onChange={(e) => updateNonGameRow(idx, "sku", e.target.value)} placeholder="e.g. NG-CTRL-001" data-testid={`inv-nongame-sku-${idx}`} />
                  </div>
                  <div className="sm:col-span-12 flex justify-end">
                    {nonGameRows.length > 1 && <button onClick={() => removeNonGameRow(idx)} className="label-eyebrow hover:text-[var(--ab-danger)]" data-testid={`inv-nongame-remove-${idx}`}>Remove</button>}
                  </div>
                </div>
              ))}
            </div>
            {nonGameErr && (
              <div className="mt-3 text-sm" style={{ color: "var(--ab-danger)" }} data-testid="inv-nongame-err">{nonGameErr}</div>
            )}
            <div className="mt-4 flex gap-3 justify-between">
              <button onClick={addNonGameRow} className="btn-secondary inline-flex items-center gap-2" data-testid="inv-nongame-add-row"><Plus size={14} /> Add Another</button>
              <button onClick={confirmAddNonGame} disabled={addingNonGame} className="btn-primary inline-flex items-center gap-2" data-testid="inv-nongame-confirm">{addingNonGame ? <Loader2 size={14} className="animate-spin" /> : "Confirm"}</button>
            </div>
          </div>
        </div>
      )}
      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={onScanned} />
    </Layout>
  );
}
